import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router";

import { ApiError, getDocument, listRuns } from "@/api/client";
import { finishedPath } from "@/flow/progress";
import { buttonClass } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { Spinner } from "@/ui/primitives";

/** The step a set should open at: where its work stands, not where it began. */
export function stepPath(
  documentId: string,
  status: string,
  run: { id: string; status: string; mode_chosen?: string | null } | null,
): string {
  const reading = status === "uploaded" || status === "ingesting";
  if (reading || status === "failed" || !run) {
    return `/documents/${documentId}/sheets`;
  }
  if (run.status === "done") {
    return finishedPath(documentId, run);
  }
  return `/documents/${documentId}/runs/${run.id}/progress`;
}

/** `/documents/:id` has no screen of its own; it sends the estimator to the right step. */
export function DocumentResolver() {
  const { id = "" } = useParams();
  const [to, setTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const doc = await getDocument(id);
        if (doc.kind !== "drawing_set") {
          // Past work is not taken off; it lives with the organization's knowledge.
          if (!cancelled) setTo(`/knowledge/sources/${id}`);
          return;
        }
        const runs = await listRuns(id).catch(() => []);
        if (!cancelled) {
          setTo(stepPath(id, doc.status, runs[0] ?? null));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.detail : "could not reach the API");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (to) {
    return <Navigate to={to} replace />;
  }
  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-4 p-6">
      {error ? (
        <>
          <Notice tone="danger" className="w-full">
            {error}
          </Notice>
          <Link to="/sets" className={buttonClass("secondary")}>
            Back to your sets
          </Link>
        </>
      ) : (
        <p className="flex items-center gap-2 text-muted" role="status">
          <Spinner />
          Opening…
        </p>
      )}
    </main>
  );
}
